import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import CustomObjectListWidget from './CustomObjectListWidget';

const formatDateLabel = (value, intl, { hideYear = false } = {}) => {
  if (!value) return null;

  try {
    const parsed = value instanceof Date ? value : new Date(value);

    if (Number.isNaN(parsed.getTime())) {
      return value;
    }

    if (!intl || typeof intl.formatDate !== 'function') {
      const iso = parsed.toISOString();
      if (hideYear) {
        return iso.slice(5, 10);
      }
      return iso.slice(0, 10);
    }

    const formatOptions = hideYear
      ? {
          month: 'short',
          day: 'numeric',
        }
      : {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
        };

    return intl.formatDate(parsed, formatOptions);
  } catch (e) {
    return value;
  }
};

const messages = defineMessages({
  addDateRange: {
    id: 'Add Date Range',
    defaultMessage: 'Add Date Range',
  },
  fromDate: {
    id: 'From Date',
    defaultMessage: 'From Date',
  },
  repeatEveryYear: {
    id: 'Repeat every Year',
    defaultMessage: 'Repeat every Year',
  },
  toDate: {
    id: 'To Date',
    defaultMessage: 'To Date',
  },
});

const dateRangesSchema = (intl) => ({
  title: 'Date Ranges',
  addMessage: intl.formatMessage(messages.addDateRange),
  properties: {
    fromDate: {
      title: intl.formatMessage(messages.fromDate),
      type: 'date',
      widget: 'date',
    },
    toDate: {
      title: intl.formatMessage(messages.toDate),
      type: 'date',
      widget: 'date',
    },
    repeatEveryYear: {
      title: intl.formatMessage(messages.repeatEveryYear),
      type: 'boolean',
    },
  },
  fieldsets: [
    {
      id: 'default',
      title: 'Default',
      fields: ['fromDate', 'toDate', 'repeatEveryYear'],
    },
  ],
  required: ['fromDate', 'toDate'],
});

const DateRangesWidget = (props) => {
  const intl = useIntl();

  const getItemTitle = React.useCallback(
    ({ item, index, fallback }) => {
      const hideYear = item?.repeatEveryYear === true;
      const fromLabel = formatDateLabel(item?.fromDate, intl, { hideYear });
      const toLabel = formatDateLabel(item?.toDate, intl, { hideYear });

      const baseTitle = (() => {
        if (fromLabel && toLabel) {
          return `${fromLabel} - ${toLabel}`;
        }

        if (fromLabel || toLabel) {
          return fromLabel || toLabel;
        }

        return (
          fallback ||
          `${intl.formatMessage(messages.addDateRange)} #${index + 1}`
        );
      })();

      if (item?.repeatEveryYear) {
        return `${baseTitle} • ${intl.formatMessage(messages.repeatEveryYear)}`;
      }

      return baseTitle;
    },
    [intl],
  );

  return (
    <CustomObjectListWidget
      schema={dateRangesSchema(intl)}
      getItemTitle={getItemTitle}
      {...props}
      value={props.value?.items || props.default?.items || []}
      onChange={(id, value) => props.onChange(id, { items: value })}
    />
  );
};

export default DateRangesWidget;
